import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { AppLink } from '~/components/AppLink'
import { GoogleButton } from '~/components/GoogleButton'

import s from './Claim.module.css'

/**
 * Taking ownership of an account that was made before addresses were asked for.
 *
 * Six accounts exist that this site cannot reach from the inside: no address, nothing that
 * is *theirs* rather than something they chose here. A claim link is the way in from
 * outside — handed over by somebody who knows them personally, which is the only evidence
 * there will ever be about whose row this is.
 *
 * **Pressing the link does not spend it.** This page reads whose account it is and offers a
 * way to take it; the link is spent when Google answers. Otherwise opening the message
 * twice, or a link preview fetching it, would burn it before anybody decided anything.
 *
 * The account keeps everything — saves, going marks, friends, username — and the password
 * keeps working. This adds an identity rather than replacing one.
 */

const TROUBLE: Record<string, string> = {
  unverified: 'Google has not verified that address',
  'already-linked': 'that Google account is already signed in to another account here',
  'address-in-use': 'another account here already uses that address',
  stale: 'that link has been used, or has expired',
}

export function Claim() {
  const { token = '' } = useParams()
  const [username, setUsername] = useState<string | null>(null)
  const [gone, setGone] = useState(false)
  const [trouble, setTrouble] = useState('')

  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get('trouble')
    if (code) setTrouble(TROUBLE[code] ?? 'that did not work')
  }, [])

  useEffect(() => {
    let live = true
    fetch(`/claiming/${encodeURIComponent(token)}`, { credentials: 'same-origin' })
      .then(answer => (answer.ok ? answer.json() : Promise.reject(new Error('stale'))))
      .then((found: { username: string }) => {
        if (live) setUsername(found.username)
      })
      .catch(() => {
        if (live) setGone(true)
      })
    return () => {
      live = false
    }
  }, [token])

  return (
    <div className={s.page}>
      <div className={s.sky} aria-hidden="true" />
      <main className={s.main}>
        <h1>{gone ? 'London gigs' : 'Claim your account'}</h1>
        <div className={s.form}>
          <p className={s.err}>{trouble}</p>

          {gone ? (
            <p className={s.note}>
              That link has been used, or has expired. Ask Bernardo for another.
            </p>
          ) : (
            <>
              <p className={s.who}>
                {username ? (
                  <>
                    This is <strong>{username}</strong>. Sign in with Google to make it
                    yours — everything you have saved stays where it is.
                  </>
                ) : (
                  'One moment…'
                )}
              </p>

              {username ? (
                <GoogleButton href={`/auth/google?claim=${encodeURIComponent(token)}`} />
              ) : null}
            </>
          )}
          {/* On the card rather than under it: centring the page inside the frame put this
              line on a chrome highlight, and pale type on a pale reflection is unreadable. */}
          <p className={s.back}>
            <AppLink className={s.home} to="/">
              Back to the gigs
            </AppLink>
          </p>
        </div>

      </main>
    </div>
  )
}
